import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/components/AuthProvider";
import { type UserRole } from "@/types/enums";
import {
  Calendar,
  ClipboardList,
  Users,
  BarChart3,
  Link2,
  Settings,
  Store,
  UserPlus,
  ExternalLink,
  Plus,
  Minus,
  X,
  Bug,
} from "lucide-react";

interface SubMenuItem {
  id: string;
  label: string;
  to: string;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ElementType;
  to?: string;
  expandable?: boolean;
  subItems?: SubMenuItem[];
  roles?: UserRole[];
}

/**
 * Menu structure matching the legacy CI4 system sidebar.
 *
 * v2 role access (sample / general roles dropped from v2):
 *   root   – all items
 *   admin  – all items except メンテナンス (not in v2)
 *   shop   – 予約表〜集計, 強制リンク, 基本マスター, 店舗マスタ, スタッフ登録, 外部連携
 *   user   – 予約表〜集計, 強制リンク(limited), マスター(limited)
 */
const menuItems: MenuItem[] = [
  // ── 01 予約表 ──
  { id: "reservations", label: "予約表", icon: Calendar, to: "/" },

  // ── 02 出勤表 ──
  {
    id: "attendance",
    label: "出勤表",
    icon: ClipboardList,
    to: "/shifts",
    roles: ["root", "admin", "shop", "user"],
  },

  // ── 03 顧客管理 ──
  {
    id: "customers",
    label: "顧客管理",
    icon: Users,
    to: "/customers",
    roles: ["root", "admin", "shop", "user"],
  },

  // ── 04 集計 ──
  {
    id: "reports",
    label: "集計",
    icon: BarChart3,
    to: "/reports",
    expandable: true,
    roles: ["root", "admin", "shop", "user"],
    subItems: [
      { id: "reports-sales", label: "売上集計", to: "/reports?tab=monthly" },
      { id: "reports-ads", label: "広告集計", to: "/ad-reports" },
    ],
  },

  // ── 05 強制リンク作成 (= 予約URL管理 + テンプレート) ──
  {
    id: "links",
    label: "強制リンク作成",
    icon: Link2,
    to: "/booking-urls",
    expandable: true,
    roles: ["root", "admin", "shop", "user"],
    subItems: [
      { id: "links-create", label: "強制リンク作成", to: "/booking-urls" },
      { id: "links-input-tpl", label: "個人情報入力画面テンプレート", to: "/integrations?tab=templates&type=input" },
      { id: "links-confirm-tpl", label: "確認画面テンプレート", to: "/integrations?tab=templates&type=confirm" },
      { id: "links-thanks-tpl", label: "サンクスページテンプレート", to: "/integrations?tab=templates&type=thanks" },
      { id: "links-media", label: "媒体マスター", to: "/integrations?tab=media" },
      { id: "links-remind-tpl", label: "リマインドテンプレート", to: "/integrations?tab=templates&type=remind" },
      { id: "links-tag-tpl", label: "タグテンプレート", to: "/integrations?tab=templates&type=tag" },
    ],
  },

  // ── 06 基本マスター ──
  {
    id: "master",
    label: "基本マスター",
    icon: Settings,
    to: "/settings",
    expandable: true,
    roles: ["root", "admin", "shop", "user"],
    subItems: [
      { id: "master-brand", label: "ブランドマスター", to: "/companies?view=brand" },
      { id: "master-cust-tag", label: "顧客タグマスター", to: "/settings?tab=tags" },
      { id: "master-menu-cat", label: "メニューカテゴリー", to: "/settings?tab=categories" },
      { id: "master-menu", label: "メニューマスター", to: "/settings?tab=menus" },
      { id: "master-sales-cat", label: "売り上げカテゴリー", to: "/settings?tab=sales-categories" },
      { id: "master-sales-type", label: "売上種類", to: "/settings?tab=payment-methods" },
    ],
  },

  // ── 07 店舗マスタ ──
  {
    id: "stores",
    label: "店舗マスタ",
    icon: Store,
    to: "/shops",
    expandable: true,
    roles: ["root", "admin", "shop"],
    subItems: [
      { id: "stores-area", label: "店舗 地域マスター", to: "/settings?tab=areas" },
      { id: "stores-master", label: "店舗 マスター", to: "/shops" },
      { id: "stores-hours", label: "店舗 営業時間設定", to: "/settings?tab=shop-hours" },
      { id: "stores-bed", label: "店舗 ベッドマスター", to: "/settings?tab=resources" },
    ],
  },

  // ── 08 スタッフ登録 ──
  {
    id: "staff",
    label: "スタッフ登録",
    icon: UserPlus,
    to: "/staff",
    expandable: true,
    roles: ["root", "admin", "shop", "user"],
    subItems: [
      { id: "staff-master", label: "店舗 スタッフマスター", to: "/staff" },
      { id: "staff-patterns", label: "店舗 出勤パターン", to: "/settings?tab=work-patterns" },
      { id: "staff-actions", label: "スタッフ行動マスター", to: "/settings?tab=action-types" },
    ],
  },

  // ── 10 外部連携 ──
  {
    id: "external",
    label: "外部連携",
    icon: ExternalLink,
    to: "/integrations",
    expandable: true,
    roles: ["root", "admin", "shop"],
    subItems: [
      { id: "ext-templates", label: "通知テンプレート", to: "/integrations?tab=templates" },
      { id: "ext-line", label: "LINEアカウント連携", to: "/integrations?tab=line" },
      { id: "ext-line-broadcast", label: "LINE送信", to: "/integrations?tab=line-broadcast" },
      { id: "ext-gcal", label: "Googleカレンダー連携", to: "/integrations?tab=gcal" },
      { id: "ext-hotpepper", label: "ホットペッパー連携", to: "/integrations?tab=hotpepper" },
      { id: "ext-mail", label: "メールテスト", to: "/integrations?tab=mail-test" },
    ],
  },

  // ── 11 バグレポート ──
  {
    id: "debug-reports",
    label: "バグレポート",
    icon: Bug,
    to: "/debug-reports",
    roles: ["root", "admin"],
  },
];

interface AppSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const AppSidebar = ({ isOpen, onClose }: AppSidebarProps) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const location = useLocation();
  const navigate = useNavigate();
  const { authUser } = useAuth();
  const userRole = authUser?.role;

  const toggleExpand = (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isItemActive = (item: MenuItem): boolean => {
    if (item.to === "/" && location.pathname === "/") return true;
    if (item.to && item.to !== "/") {
      const basePath = item.to.split("?")[0];
      if (location.pathname !== basePath && !location.pathname.startsWith(basePath + "/")) return false;
      // If item has sub-items, check if any sub-item matches
      if (item.subItems && item.subItems.length > 0) {
        const currentFull = location.pathname + location.search;
        return item.subItems.some((sub) => currentFull === sub.to) || location.pathname === basePath;
      }
      return location.pathname === basePath || location.pathname.startsWith(basePath + "/");
    }
    return false;
  };

  const isSubItemActive = (sub: SubMenuItem): boolean => {
    const currentFull = location.pathname + location.search;
    return currentFull === sub.to;
  };

  const visibleItems = menuItems.filter((item) => {
    if (!item.roles || item.roles.length === 0) return true;
    return userRole && item.roles.includes(userRole);
  });

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`
          fixed top-0 left-0 z-50 h-full w-[280px] flex flex-col bg-sidebar
          transition-transform duration-300 ease-in-out
          lg:sticky lg:top-0 lg:translate-x-0 lg:z-auto lg:h-screen lg:shrink-0
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {/* Logo header - white background */}
        <div className="bg-white px-6 py-5 flex items-center justify-between">
          <h1 className="text-[28px] font-bold tracking-wider text-foreground">
            SATTOU.
          </h1>
          <button
            onClick={onClose}
            className="lg:hidden text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav - dark background */}
        <nav className="flex-1 bg-sidebar overflow-y-auto">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            const isActive = isItemActive(item);
            const isExpanded = expandedItems.has(item.id);

            return (
              <div key={item.id}>
                {/* Main menu item */}
                <div className="border-b border-dashed border-sidebar-foreground/20">
                  <div className="flex items-center">
                    <NavLink
                      to={item.expandable ? "#" : (item.to ?? "#")}
                      end={item.to === "/"}
                      onClick={(e) => {
                        if (item.expandable) {
                          e.preventDefault();
                          toggleExpand(item.id, e);
                        } else {
                          onClose();
                        }
                      }}
                      className={`flex-1 flex items-center gap-4 px-5 py-4 text-[15px] transition-colors ${
                        isActive
                          ? "text-sidebar-primary font-medium"
                          : "text-sidebar-foreground hover:bg-sidebar-accent"
                      }`}
                    >
                      <span
                        className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${
                          isActive
                            ? "bg-sidebar-primary/20 text-sidebar-primary"
                            : "bg-sidebar-foreground/15 text-sidebar-foreground"
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                      </span>
                      <span>{item.label}</span>
                    </NavLink>

                    {item.expandable && (
                      <button
                        onClick={(e) => toggleExpand(item.id, e)}
                        className="px-4 py-4 text-sidebar-foreground/60 hover:text-sidebar-foreground transition-colors"
                      >
                        {isExpanded ? (
                          <Minus className="w-4 h-4" />
                        ) : (
                          <Plus className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* Sub items */}
                {item.expandable && isExpanded && item.subItems && (
                  <div className="bg-sidebar-accent/60">
                    {item.subItems.map((sub) => {
                      const subActive = isSubItemActive(sub);
                      return (
                        <NavLink
                          key={sub.id}
                          to={sub.to}
                          onClick={onClose}
                          className={`block px-5 pl-[4.5rem] py-3 text-sm transition-colors ${
                            subActive
                              ? "bg-foreground/90 text-sidebar-primary font-medium"
                              : "text-sidebar-foreground border-b border-sidebar-foreground/10 hover:bg-sidebar-accent"
                          }`}
                        >
                          {sub.label}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="bg-sidebar px-5 py-4 border-t border-sidebar-foreground/20">
          <button
            onClick={() => { navigate("/changelog"); onClose(); }}
            className="w-full bg-sidebar-accent/80 rounded px-3 py-2 hover:bg-sidebar-accent transition-colors cursor-pointer text-left"
          >
            <p className="text-xs text-sidebar-foreground">変更履歴/お知らせ</p>
          </button>
          <p className="text-xs text-sidebar-primary/70 mt-2 px-1">
            Ver. 25.12.28-22.12.07
          </p>
        </div>
      </aside>
    </>
  );
};

export default AppSidebar;
